/**
 * Admin Plans page — slice 2 of 2 of the redesign (design handoff:
 * `design_handoff_admin_plans/README.md`, §4 "Editor drawer").
 *
 * Replaces the Edit packaging, Link Stripe Price, Subscribers and Add-on
 * Stripe price dialogs that used to live inline in `AdminPlansPage.tsx` with
 * one right-side drawer. `AdminPlansPage` still owns the quick "Unlink
 * Stripe price" row action (More menu → ConfirmDialog) and the plan list /
 * add-on card queries; this component owns the drawer's own draft state,
 * its Stripe-prices query and its three save mutations.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Switch,
  Tab,
  Tabs,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Close, OpenInNew, Check as CheckIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BillingPlan, PlanLimits, PLAN_PACKAGING_FEATURES, RANKED_LIMITS } from '../../types/billing';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { ChipInput, ConfirmDialog, InlineMessage, NumberField, StatusChip } from '../ui';
import { tokens } from '../../theme/tokens';

export type BillingIntervalLabel = 'monthly' | 'yearly' | 'one_time' | 'lifetime';

export interface StripePriceOption {
  id: string;
  productId: string;
  productName: string;
  motzuKey: string | null;
  amount: number;
  currency: string;
  interval: 'monthly' | 'yearly' | 'one_time';
}

/** Which add-on the drawer is editing in "add-on mode". */
export type AddonPriceTarget =
  | { kind: 'addon'; key: string; name: string; currency: string; interval: BillingIntervalLabel }
  | { kind: 'integration'; name: string; currency: string; interval: BillingIntervalLabel };

/** Everything the add-on summary box needs, beyond the bare `AddonPriceTarget`. */
export interface AddonDrawerSummary {
  target: AddonPriceTarget;
  price: number;
  availableOnTiers: string[];
  includedOnTiers: string[];
  stripePriceId?: string;
  comingSoon?: boolean;
}

export interface PlanSubscriberRow {
  id: string;
  planId: string;
  organizationId: string;
  organizationName: string;
  status: string;
  stripeSubscriptionId: string | null;
  stripeUrl: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** The subscription that currently governs a counted store; only these are counted as the plan's stores. */
  counted?: boolean;
}

export type DrawerTab = 'packaging' | 'limits' | 'stripe' | 'subs';

/**
 * Billing cadence, shared with the plan row's price cell (Finding 4 asked us
 * to reuse this rather than add a second formatter).
 */
export const cadenceSuffix = (interval: 'monthly' | 'yearly' | 'one_time' | 'lifetime'): string =>
  interval === 'one_time' || interval === 'lifetime' ? 'one-time' : interval === 'yearly' ? '/ yr' : '/ mo';

const priceOptionAmountLabel = (o: StripePriceOption) =>
  o.amount.toLocaleString('en-US', { style: 'currency', currency: o.currency, maximumFractionDigits: 0 });

/** The Stripe price the setup script created for this plan/add-on: matched
 * on motzu_key first ("tier:pro", "setup_fee:pro"), then on product name. */
const suggestStripePrice = (
  name: string,
  options: StripePriceOption[],
  kind: 'plan' | 'setup_fee',
  currency: string,
): StripePriceOption | undefined => {
  const wantInterval = kind === 'plan' ? 'monthly' : 'one_time';
  const candidates = options.filter(o => o.interval === wantInterval && o.currency === currency);
  return (
    candidates.find(o => o.motzuKey === `${kind === 'plan' ? 'tier' : 'setup_fee'}:${name}`) ||
    candidates.find(o =>
      kind === 'plan'
        ? o.productName.trim().toLowerCase() === name
        : o.productName.trim().toLowerCase().replace(/\s*[—–-]?\s*setup fee$/, '') === name,
    )
  );
};

export const shortPriceId = (id: string) => (id.length > 20 ? `${id.slice(0, 12)}…${id.slice(-5)}` : id);

export const formatAddonMoney = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `$${amount}`;
  }
};

export const titleCase = (s: string) => s.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export const availabilitySentence = (row: { availableOnTiers: string[]; includedOnTiers: string[] }) => {
  const parts: string[] = [];
  if (row.availableOnTiers.length) parts.push(`Sold on ${row.availableOnTiers.map(titleCase).join(', ')}`);
  if (row.includedOnTiers.length) parts.push(`Included in ${row.includedOnTiers.map(titleCase).join(', ')}`);
  return parts.join(' · ') || 'Not sold on any tier';
};

const EMPTY_LIMITS: PlanLimits = {
  maxUsers: 1,
  maxApiCalls: 100,
  maxDataSync: -1,
  productSyncCap: false,
  maxSyncBrands: -1,
  maxIntegrations: 0,
  realTimeSync: false,
  mapViolationAlerts: false,
  advancedAnalytics: false,
  prioritySupport: false,
  customIntegrations: false,
  whiteLabel: false,
  inventorySync: false,
  rebatePricing: false,
  rebateTracking: false,
  automatedRebates: false,
  customPricing: false,
  crossIntegrationPricingLegacy: false,
  customApi: false,
  aiClassification: false,
  dealerProductView: false,
  specMappingCrosswalk: false,
  dedicatedSupport: false,
  reportLevel: 'basic',
  variablePricingLevel: 'basic',
  manufacturerReportLevel: 'basic',
  mapAuditLevel: 'basic',
  // A missing key reads as capped on the server; the editor shows the same.
  maxMapAuditProducts: 100,
  aiContentTools: false,
  aiBulkMonthlyOperations: 0,
};

const CURRENCY_CODES = ['USD', 'CAD'];
/** The sheet currencies, plus whatever the plan already carries so an older value is never dropped. */
const currencyOptions = (current: string): string[] =>
  current && !CURRENCY_CODES.includes(current) ? [...CURRENCY_CODES, current] : CURRENCY_CODES;

/** Blank is the plan's default price; CA is the regional CAD price slot. */
const regionOptions = (current: string): Array<{ value: string; label: string }> => {
  const base = [
    { value: '', label: 'Default price' },
    { value: 'CA', label: 'CA (regional CAD price)' },
  ];
  return current && !base.some(o => o.value === current) ? [...base, { value: current, label: current }] : base;
};

const NUMERIC_LIMITS: Array<{ key: keyof PlanLimits; label: string; helper: string }> = [
  { key: 'maxUsers', label: 'Max users', helper: '-1 = unlimited' },
  { key: 'maxApiCalls', label: 'Max API calls / month', helper: '-1 = unlimited' },
  { key: 'maxIntegrations', label: 'Max integrations', helper: '-1 = unlimited' },
  // Flag map_audit_ladder: mapped products one MAP audit run checks (Free Preview 100).
  { key: 'maxMapAuditProducts', label: 'Max products per MAP audit run', helper: '-1 = unlimited' },
  // Flag bulk_ai_operations: model calls a month; sold with the aiContentTools switch.
  { key: 'aiBulkMonthlyOperations', label: 'Bulk AI model calls / month', helper: '0 = none, -1 = unlimited' },
];

const REPORT_LEVELS: Array<{ value: NonNullable<PlanLimits['reportLevel']>; label: string }> = [
  { value: 'basic', label: 'Basic' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'complete', label: 'Complete' },
];

const normalizeLimits = (limits?: PlanLimits): PlanLimits => {
  const merged = { ...EMPTY_LIMITS, ...(limits || {}) };
  // Legacy Complete plans may still have reportLevel: 'enterprise' in Firestore.
  for (const { key } of RANKED_LIMITS) {
    if ((merged[key] as string) === 'enterprise') merged[key] = 'complete';
  }
  return merged;
};

interface PlanDraft {
  name: string;
  description: string;
  whoItsFor: string;
  features: string;
  price: string;
  currency: string;
  isActive: boolean;
  isPopular: boolean;
  setupFee: string;
  setupFeeCustom: boolean;
  setupFeeStripePriceId: string;
  limits: PlanLimits;
  stripePriceId: string;
  /**
   * The Region field from the old Link Stripe Price dialog. Not in the
   * design handoff's Stripe tab spec, but §"Regional Stripe price" says
   * never to remove the ability to write/clear a regional (CA) price from
   * this page — see the drawer's Stripe tab for the note on this deviation.
   */
  region: string;
  syncDisplayPrice: boolean;
}

const draftFromPlan = (plan: BillingPlan): PlanDraft => ({
  name: plan.name || '',
  description: plan.description || '',
  whoItsFor: plan.whoItsFor || '',
  features: (plan.features || []).join('\n'),
  price: String(plan.price ?? 0),
  currency: plan.currency || 'USD',
  isActive: plan.isActive !== false,
  isPopular: !!plan.isPopular,
  setupFee: typeof plan.setupFee === 'number' ? String(plan.setupFee) : '',
  setupFeeCustom: plan.setupFeeCustom === true,
  setupFeeStripePriceId: plan.setupFeeStripePriceId || '',
  limits: normalizeLimits(plan.limits),
  stripePriceId: plan.stripePriceId || '',
  region: '',
  syncDisplayPrice: true,
});

interface AddonDraft {
  stripePriceId: string;
}

const draftFromAddon = (addon: AddonDrawerSummary): AddonDraft => ({ stripePriceId: addon.stripePriceId || '' });

/**
 * The one tier that legitimately has no Stripe price, matched the same way
 * `FEATURE_TIERS` matches it (`src/types/billing.ts`, `planNameMatch: 'free
 * preview'`), not by `price > 0` (see CLAUDE.md "Free Preview is the one
 * tier..."). Exported so `AdminPlansPage` shares this instead of a second
 * copy.
 */
export const isFreePreviewPlan = (plan: BillingPlan) => plan.name.trim().toLowerCase() === 'free preview';

// Currency the plan Stripe price picker filters and suggests against. A
// named region means that region's currency (`regionalPrices.CA` is CAD);
// blank means the plan's own default price, which on the Canada stack is
// already CAD (see CLAUDE.md "Subscription currency by stack").
const stripeLinkCurrency = (plan: BillingPlan | null, region: string) =>
  region.trim().toUpperCase() === 'CA' ? 'CAD' : plan?.currency || 'USD';

/** A single row option in a Stripe price picker's popover list. */
interface PickerOption {
  price: StripePriceOption | null; // null = "Not linked"
  nameMatch?: boolean;
}

/**
 * The §4 "Stripe price dropdown": an 8px status dot + name/amount/id closed
 * trigger, a searchable popover (MUI `Autocomplete`'s own filtering, which
 * matches name/amount/id together since they're all in the option label),
 * "Not linked" always last, and the `suggestStripePrice()` match tagged
 * "Name match" and sorted first rather than silently auto-selected.
 */
const StripePricePicker: React.FC<{
  options: StripePriceOption[];
  value: string;
  onChange: (id: string) => void;
  loading?: boolean;
  suggestion?: StripePriceOption;
  caption: string;
  readError?: boolean;
}> = ({ options, value, onChange, loading, suggestion, caption, readError }) => {
  const ordered = useMemo(() => {
    const rest = suggestion ? options.filter(o => o.id !== suggestion.id) : options;
    const pricedOptions: PickerOption[] = [
      ...(suggestion ? [{ price: suggestion, nameMatch: true }] : []),
      ...rest.map(price => ({ price })),
    ];
    return [...pricedOptions, { price: null }];
  }, [options, suggestion]);

  // When nothing is linked, leave Autocomplete's value null (empty input) so
  // opening the dropdown does not filter the list by the "Not linked" label.
  // "Not linked" stays in the options list for clearing a linked price.
  const selected = value ? ordered.find(o => o.price?.id === value) || null : null;
  const linked = !!value;

  return (
    <Box>
      <Autocomplete
        options={ordered}
        loading={loading}
        value={selected}
        isOptionEqualToValue={(o, v) => o.price?.id === v.price?.id}
        getOptionLabel={o => (o.price ? `${o.price.productName} ${priceOptionAmountLabel(o.price)} ${o.price.id}` : 'Not linked')}
        onChange={(_, v) => onChange(v?.price?.id || '')}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.price?.id || '__not_linked'}>
            <Stack sx={{ width: '100%', py: 0.25 }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: option.price ? tokens.ink.primary : tokens.signal.caution.ink }}>
                  {option.price ? option.price.productName : 'Not linked'}
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  {option.nameMatch && <StatusChip label="Name match" tone="positive" size="small" />}
                  {value === option.price?.id && <CheckIcon sx={{ fontSize: 16, color: tokens.accent.main }} />}
                </Stack>
              </Stack>
              <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>
                {option.price ? priceOptionAmountLabel(option.price) : "Stores can't be charged"}
              </Typography>
              {option.price && (
                <Typography sx={{ fontSize: 11.5, color: tokens.ink.faint, fontFamily: tokens.font.mono }}>{option.price.id}</Typography>
              )}
            </Stack>
          </Box>
        )}
        renderInput={params => (
          <Box ref={params.InputProps.ref} sx={{ position: 'relative' }}>
            <TextField
              {...params}
              placeholder="Search name, amount or price_ ID"
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: 52,
                  borderRadius: '9px',
                  fontSize: 13.5,
                  pl: '30px',
                  '& fieldset': { borderColor: linked ? tokens.line.strong : tokens.signal.caution.solid },
                  '&.Mui-focused fieldset': { borderColor: tokens.accent.main },
                },
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: linked ? tokens.signal.positive.solid : tokens.signal.caution.solid,
              }}
            />
          </Box>
        )}
      />
      <Typography sx={{ fontSize: 11.5, color: tokens.ink.faint, mt: 0.5 }}>
        {readError ? 'Stripe could not be read; paste the price_... id instead.' : caption}
      </Typography>
    </Box>
  );
};

interface PlanEditorDrawerProps {
  open: boolean;
  mode: 'plan' | 'addon' | null;
  requestedTab: DrawerTab;
  plan: BillingPlan | null;
  addon: AddonDrawerSummary | null;
  subscribers: PlanSubscriberRow[];
  /** Whether the Stripe tab should show the §4 amber issue dot. */
  hasStripeIssue: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
  onOpenMoveSubscribers: (plan: BillingPlan) => void;
}

const inputLabelSx = { fontSize: 12.5, fontWeight: 600, color: tokens.ink.body, mb: '6px', display: 'block' };
const helperSx = { fontSize: 12, color: tokens.ink.faint, mt: '4px' };

export const PlanEditorDrawer: React.FC<PlanEditorDrawerProps> = ({
  open,
  mode,
  requestedTab,
  plan,
  addon,
  subscribers,
  hasStripeIssue,
  onClose,
  onSaved,
  onOpenMoveSubscribers,
}) => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<DrawerTab>('packaging');
  const [planDraft, setPlanDraft] = useState<PlanDraft | null>(null);
  const [addonDraft, setAddonDraft] = useState<AddonDraft | null>(null);
  const [initialSnapshot, setInitialSnapshot] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  // Add-on unlink stays behind a confirm (PR 401/PR 402 pattern) even though the
  // §4 spec folds the old row-level unlink icon into "choose Not linked in
  // the dropdown" with no separate confirm — see the PR description's
  // conflict note. We only ask when Save would actually clear a price that
  // was linked, not on every save.
  const [addonUnlinkConfirmOpen, setAddonUnlinkConfirmOpen] = useState(false);
  const [addonUnlinkError, setAddonUnlinkError] = useState<string | null>(null);

  // Reset the draft whenever the drawer opens for a (possibly different) target.
  const openKey = mode === 'plan' ? plan?.id : mode === 'addon' ? (addon?.target.kind === 'addon' ? addon.target.key : 'integration') : null;
  useEffect(() => {
    if (!open) return;
    setFormError(null);
    setDiscardConfirmOpen(false);
    setAddonUnlinkConfirmOpen(false);
    setTab(requestedTab);
    if (mode === 'plan' && plan) {
      const draft = draftFromPlan(plan);
      setPlanDraft(draft);
      setAddonDraft(null);
      setInitialSnapshot(JSON.stringify(draft));
    } else if (mode === 'addon' && addon) {
      const draft = draftFromAddon(addon);
      setAddonDraft(draft);
      setPlanDraft(null);
      setInitialSnapshot(JSON.stringify(draft));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, openKey, mode]);

  const stripePricesQuery = useQuery({
    queryKey: ['admin-stripe-prices'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/billing/admin/stripe-prices', { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) throw new Error(body?.message || body?.error?.message || 'Could not read Stripe prices');
      return body.data as StripePriceOption[];
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const stripePrices = stripePricesQuery.data || [];

  const priceOptionsFor = (kind: 'plan' | 'setup_fee', currency?: string) =>
    stripePrices.filter(o => (kind === 'plan' ? o.interval !== 'one_time' : o.interval === 'one_time') && (!currency || o.currency === currency));

  const priceOptionsForAddon = (interval: BillingIntervalLabel, currency: string) =>
    stripePrices.filter(o => o.interval === interval && o.currency === currency);

  const dirty = useMemo(() => {
    if (mode === 'plan' && planDraft) return JSON.stringify(planDraft) !== initialSnapshot;
    if (mode === 'addon' && addonDraft) return JSON.stringify(addonDraft) !== initialSnapshot;
    return false;
  }, [mode, planDraft, addonDraft, initialSnapshot]);

  const packagingChanged = (draft: PlanDraft, original: BillingPlan): boolean => {
    const originalDraft = draftFromPlan(original);
    return (
      draft.name !== originalDraft.name ||
      draft.description !== originalDraft.description ||
      draft.whoItsFor !== originalDraft.whoItsFor ||
      draft.features !== originalDraft.features ||
      draft.price !== originalDraft.price ||
      draft.currency !== originalDraft.currency ||
      draft.isActive !== originalDraft.isActive ||
      draft.isPopular !== originalDraft.isPopular ||
      draft.setupFee !== originalDraft.setupFee ||
      draft.setupFeeCustom !== originalDraft.setupFeeCustom ||
      draft.setupFeeStripePriceId !== originalDraft.setupFeeStripePriceId ||
      JSON.stringify(draft.limits) !== JSON.stringify(originalDraft.limits)
    );
  };

  const savePlanMutation = useMutation({
    mutationFn: async () => {
      if (!plan || !planDraft) throw new Error('No plan selected');
      const features = planDraft.features.split('\n').map(l => l.trim()).filter(Boolean);
      const price = Number(planDraft.price);
      if (Number.isNaN(price) || price < 0) throw new Error('Display price must be a non-negative number');
      const setupFeeText = planDraft.setupFee.trim();
      const setupFee = setupFeeText === '' ? null : Number(setupFeeText);
      if (setupFee !== null && (Number.isNaN(setupFee) || setupFee < 0)) {
        throw new Error('Setup fee must be a non-negative number, or blank when the fee is custom quoted');
      }
      if (planDraft.setupFeeCustom && setupFee !== null && setupFee > 0) {
        throw new Error('Clear the setup fee amount when the fee is custom quoted');
      }
      const setupFeePriceId = planDraft.setupFeeStripePriceId.trim();
      if (setupFeePriceId && !/^price_[A-Za-z0-9]+$/.test(setupFeePriceId)) {
        throw new Error('Setup fee Stripe price must look like price_...');
      }
      const headers = await getAuthHeaders();

      // 1. Packaging (name, limits, price, setup fee, active/popular).
      if (packagingChanged(planDraft, plan)) {
        const res = await makeApiCall(`/billing/admin/plans/${plan.id}`, {
          method: 'PATCH',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: planDraft.name.trim(),
            description: planDraft.description.trim(),
            whoItsFor: planDraft.whoItsFor.trim(),
            features,
            limits: planDraft.limits,
            isActive: planDraft.isActive,
            isPopular: planDraft.isPopular,
            price,
            currency: planDraft.currency.trim().toUpperCase() || 'USD',
            setupFee,
            setupFeeCustom: planDraft.setupFeeCustom,
            setupFeeStripePriceId: setupFeePriceId || null,
          }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.message || 'Failed to update plan');
      }

      // 2/3. Monthly (or regional) Stripe price — link, change or clear.
      // When Region is set, diff against that regional slot — not the default
      // stripePriceId — or clearing a CA price while the default is already
      // empty would no-op.
      const region = planDraft.region.trim();
      const regionKey = region.toUpperCase();
      const newPriceId = planDraft.stripePriceId.trim();
      const originalPriceId = regionKey
        ? plan.regionalPrices?.[regionKey]?.stripePriceId || ''
        : plan.stripePriceId || '';
      if (newPriceId && newPriceId !== originalPriceId) {
        const res = await makeApiCall(`/billing/admin/plans/${plan.id}/stripe-price`, {
          method: 'PUT',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            stripePriceId: newPriceId,
            region: region || undefined,
            syncDisplayPrice: planDraft.syncDisplayPrice,
          }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.message || 'Failed to link Stripe price');
      } else if (!newPriceId && originalPriceId) {
        const res = await makeApiCall(`/billing/admin/plans/${plan.id}/stripe-price`, {
          method: 'DELETE',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(region ? { region } : {}),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.message || 'Failed to unlink Stripe price');
      }

      return { message: 'Plan saved' };
    },
    onSuccess: body => {
      queryClient.invalidateQueries({ queryKey: ['admin-billing-plans'] });
      onSaved(body.message);
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const saveAddonMutation = useMutation({
    mutationFn: async (stripePriceId: string | null) => {
      if (!addon) throw new Error('No add-on selected');
      const headers = await getAuthHeaders();
      const path =
        addon.target.kind === 'addon'
          ? `/billing/admin/plan-addons/${addon.target.key}/stripe-price`
          : '/billing/admin/integration-addon/stripe-price';
      const res = await makeApiCall(path, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stripePriceId }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.success) throw new Error(body?.message || body?.error?.message || 'Failed to update the Stripe price');
      return body as { message?: string };
    },
    onSuccess: body => {
      queryClient.invalidateQueries({ queryKey: ['admin-plan-addons'] });
      queryClient.invalidateQueries({ queryKey: ['admin-integration-addon'] });
      setAddonUnlinkConfirmOpen(false);
      setAddonUnlinkError(null);
      onSaved(body.message || 'Stripe price updated');
    },
    // The confirm dialog runs with `stripePriceId: null` (clearing a linked
    // price); its error surfaces inside that confirm, not the drawer
    // footer — same "stays open, shows the error inline" pattern as the
    // plan-row Unlink confirm in AdminPlansPage (PR 402, Finding 7).
    onError: (err: Error, stripePriceId) => {
      if (stripePriceId === null) setAddonUnlinkError(err.message);
      else setFormError(err.message);
    },
  });

  const handleSave = () => {
    setFormError(null);
    if (mode === 'plan') {
      savePlanMutation.mutate();
    } else if (mode === 'addon' && addonDraft && addon) {
      const clearing = !!addon.stripePriceId && !addonDraft.stripePriceId;
      if (clearing) {
        setAddonUnlinkConfirmOpen(true);
        return;
      }
      saveAddonMutation.mutate(addonDraft.stripePriceId || null);
    }
  };

  const saving = savePlanMutation.isPending || saveAddonMutation.isPending;

  const requestClose = () => {
    if (saving) return;
    if (dirty) {
      setDiscardConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const eyebrow = mode === 'addon' ? 'ADD-ON · STRIPE PRICE' : 'EDIT PLAN';
  const title = mode === 'plan' ? plan?.name : addon?.target.name;
  const docId = mode === 'plan' ? plan?.id : addon?.target.kind === 'addon' ? addon.target.key : 'additional_integration';

  const tabs: Array<{ key: DrawerTab; label: string }> = [
    { key: 'packaging', label: 'Packaging' },
    { key: 'limits', label: 'Limits & features' },
    { key: 'stripe', label: 'Stripe' },
    { key: 'subs', label: `Subscribers (${subscribers.length})` },
  ];

  const onFeatureCount = planDraft ? PLAN_PACKAGING_FEATURES.filter(f => !!planDraft.limits[f.key]).length : 0;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={requestClose}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 560 }, maxWidth: '100vw', boxShadow: tokens.shadow.overlay } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ p: '18px 22px 14px', borderBottom: `1px solid ${tokens.line.hairline}` }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: tokens.ink.faint }}>
                  {eyebrow}
                </Typography>
                <Typography sx={{ fontSize: 19, fontWeight: 700, color: tokens.ink.primary }}>{title}</Typography>
                <Typography sx={{ fontSize: 11.5, color: tokens.ink.faint, fontFamily: tokens.font.mono }}>{docId}</Typography>
              </Box>
              <IconButton size="small" onClick={requestClose} disabled={saving}>
                <Close fontSize="small" />
              </IconButton>
            </Stack>
            {mode === 'plan' && (
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                  mt: 1,
                  minHeight: 38,
                  '& .MuiTab-root': {
                    minHeight: 38,
                    fontSize: 13,
                    fontWeight: 600,
                    color: tokens.ink.muted,
                    textTransform: 'none',
                    '&.Mui-selected': { color: tokens.ink.primary },
                  },
                  '& .MuiTabs-indicator': { height: 2, bgcolor: tokens.accent.main },
                }}
              >
                {tabs.map(t => (
                  <Tab
                    key={t.key}
                    value={t.key}
                    label={
                      t.key === 'stripe' && hasStripeIssue ? (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <span>{t.label}</span>
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: tokens.signal.caution.solid }} />
                        </Stack>
                      ) : (
                        t.label
                      )
                    }
                  />
                ))}
              </Tabs>
            )}
          </Box>

          {/* Body */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: '20px 22px 28px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {mode === 'plan' && planDraft && plan && tab === 'packaging' && (
              <Stack spacing={2.5}>
                <Box>
                  <Typography sx={inputLabelSx}>Plan name</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={planDraft.name}
                    onChange={e => setPlanDraft(d => (d ? { ...d, name: e.target.value } : d))}
                  />
                </Box>
                <Box>
                  <Typography sx={inputLabelSx}>Who it&apos;s for</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={planDraft.whoItsFor}
                    onChange={e => setPlanDraft(d => (d ? { ...d, whoItsFor: e.target.value } : d))}
                  />
                  <Typography sx={helperSx}>Shown under the plan name on the customer pricing card.</Typography>
                </Box>
                <Box>
                  <Typography sx={inputLabelSx}>Description</Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Optional longer description"
                    value={planDraft.description}
                    onChange={e => setPlanDraft(d => (d ? { ...d, description: e.target.value } : d))}
                  />
                </Box>
                <Stack direction="row" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={inputLabelSx}>Display price / month</Typography>
                    <NumberField
                      aria-label="Display price / month"
                      min={0}
                      step={0.01}
                      adornment="$"
                      value={planDraft.price}
                      onChange={value => setPlanDraft(d => (d ? { ...d, price: value } : d))}
                      sx={{ width: '100%' }}
                    />
                    {planDraft.syncDisplayPrice && planDraft.stripePriceId && (
                      <Typography sx={helperSx}>
                        Follows the linked Stripe price. Turn off sync on the Stripe tab to override.
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ width: 120 }}>
                    <Typography sx={inputLabelSx}>Currency</Typography>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={planDraft.currency}
                      onChange={e => setPlanDraft(d => (d ? { ...d, currency: e.target.value.toUpperCase() } : d))}
                      slotProps={{ htmlInput: { 'aria-label': 'Currency' } }}
                    >
                      {currencyOptions(planDraft.currency).map(code => (
                        <MenuItem key={code} value={code}>{code}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Stack>
                <Box sx={{ border: `1px solid ${tokens.line.default}`, borderRadius: '10px', p: 1.5 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>Offered to stores</Typography>
                      <Typography sx={helperSx}>Off hides the plan from the pricing page. Existing subscribers keep it.</Typography>
                    </Box>
                    <Switch
                      checked={planDraft.isActive}
                      onChange={e => setPlanDraft(d => (d ? { ...d, isActive: e.target.checked } : d))}
                    />
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.5 }}>
                    <Box>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>Mark as Popular</Typography>
                      <Typography sx={helperSx}>Adds the &quot;Most popular&quot; banner on the pricing card.</Typography>
                    </Box>
                    <Switch
                      checked={planDraft.isPopular}
                      onChange={e => setPlanDraft(d => (d ? { ...d, isPopular: e.target.checked } : d))}
                    />
                  </Stack>
                </Box>
                <Box>
                  <Typography sx={inputLabelSx}>Feature bullets (one per line)</Typography>
                  <ChipInput
                    aria-label="Feature bullets"
                    commaSeparates={false}
                    value={planDraft.features.split('\n').map(l => l.trim()).filter(Boolean)}
                    onChange={next => setPlanDraft(d => (d ? { ...d, features: next.join('\n') } : d))}
                    placeholder="Type a bullet and press Enter"
                  />
                </Box>
              </Stack>
            )}

            {mode === 'plan' && planDraft && tab === 'limits' && (
              <Stack spacing={2.5}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>Usage limits</Typography>
                  <Stack spacing={1}>
                    {NUMERIC_LIMITS.map(item => {
                      const unlimited = planDraft.limits[item.key] === -1;
                      return (
                        <Box
                          key={item.key}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: '1fr 140px 110px' },
                            gap: '10px',
                            alignItems: { xs: 'stretch', sm: 'center' },
                          }}
                        >
                          <Typography sx={{ fontSize: 13, minWidth: 0 }}>{item.label}</Typography>
                          <NumberField
                            aria-label={item.label}
                            integer
                            min={0}
                            disabled={unlimited}
                            placeholder="Unlimited"
                            value={unlimited ? '' : String(planDraft.limits[item.key] as number)}
                            sx={{ bgcolor: unlimited ? tokens.surface.sunken : undefined }}
                            onChange={value =>
                              setPlanDraft(d =>
                                d ? { ...d, limits: { ...d.limits, [item.key]: Number(value) } } : d,
                              )
                            }
                          />
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Switch
                              size="small"
                              checked={unlimited}
                              onChange={e =>
                                setPlanDraft(d =>
                                  d ? { ...d, limits: { ...d.limits, [item.key]: e.target.checked ? -1 : 1 } } : d,
                                )
                              }
                            />
                            <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>Unlimited</Typography>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>

                {/*
                 * Not in the §4 spec (which lists only NUMERIC_LIMITS, the
                 * feature grid and the report-level segmented control), but
                 * dropping it would regress an existing, billing-critical
                 * editor: `productSyncCap` / `maxDataSync` / `maxSyncBrands`
                 * gate how many products/brands a store's sync selection may
                 * span (CLAUDE.md "maxDataSync is the cap..."). Kept as an
                 * extra Usage-limits subsection — see the PR description.
                 */}
                <Box sx={{ border: `1px solid ${tokens.line.default}`, borderRadius: '10px', p: 1.5 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>Cap products synced</Typography>
                    <Switch
                      checked={planDraft.limits.productSyncCap === true}
                      onChange={e =>
                        setPlanDraft(d =>
                          d
                            ? {
                                ...d,
                                limits: {
                                  ...d.limits,
                                  productSyncCap: e.target.checked,
                                  maxDataSync:
                                    e.target.checked && (d.limits.maxDataSync === -1 || !d.limits.maxDataSync)
                                      ? 1000
                                      : d.limits.maxDataSync,
                                },
                              }
                            : d,
                        )
                      }
                    />
                  </Stack>
                  {planDraft.limits.productSyncCap === true && (
                    <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
                      <NumberField
                        label="Max products synced"
                        integer
                        min={0}
                        adornment="products"
                        sx={{ width: '100%' }}
                        value={planDraft.limits.maxDataSync !== -1 ? String(planDraft.limits.maxDataSync) : ''}
                        onChange={value =>
                          setPlanDraft(d =>
                            d ? { ...d, limits: { ...d.limits, maxDataSync: Math.max(0, Number(value) || 0) } } : d,
                          )
                        }
                      />
                      <NumberField
                        label="Max brands synced"
                        integer
                        min={1}
                        adornment="brands"
                        sx={{ width: '100%' }}
                        helperText="Blank = any"
                        value={
                          typeof planDraft.limits.maxSyncBrands === 'number' && planDraft.limits.maxSyncBrands !== -1
                            ? String(planDraft.limits.maxSyncBrands)
                            : ''
                        }
                        onChange={value =>
                          setPlanDraft(d =>
                            d
                              ? {
                                  ...d,
                                  limits: {
                                    ...d.limits,
                                    maxSyncBrands: value === '' ? -1 : Math.max(1, Number(value) || 1),
                                  },
                                }
                              : d,
                          )
                        }
                      />
                    </Stack>
                  )}
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>
                    Features · {onFeatureCount} of {PLAN_PACKAGING_FEATURES.length} on
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    {PLAN_PACKAGING_FEATURES.map(item => (
                      <Stack
                        key={item.key}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ border: `1px solid ${tokens.line.default}`, borderRadius: '9px', p: '9px 11px' }}
                      >
                        <Typography sx={{ fontSize: 12.5 }}>{item.label}</Typography>
                        <Switch
                          size="small"
                          checked={!!planDraft.limits[item.key]}
                          onChange={e =>
                            setPlanDraft(d => (d ? { ...d, limits: { ...d.limits, [item.key]: e.target.checked } } : d))
                          }
                        />
                      </Stack>
                    ))}
                  </Box>
                </Box>

                {RANKED_LIMITS.map(rank => (
                <Box key={rank.key}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{rank.label}</Typography>
                  <Typography sx={{ ...helperSx, mt: 0, mb: 1 }}>{rank.helper}</Typography>
                  <Stack direction="row" sx={{ bgcolor: tokens.surface.tint, borderRadius: '9px', p: '3px', width: 'fit-content' }}>
                    {REPORT_LEVELS.map(level => {
                      const active = (planDraft.limits[rank.key] || 'basic') === level.value;
                      return (
                        <Box
                          key={level.value}
                          onClick={() => setPlanDraft(d => (d ? { ...d, limits: { ...d.limits, [rank.key]: level.value } } : d))}
                          sx={{
                            px: 2,
                            py: 0.75,
                            borderRadius: '7px',
                            fontSize: 12.5,
                            fontWeight: 600,
                            cursor: 'pointer',
                            bgcolor: active ? tokens.surface.card : 'transparent',
                            boxShadow: active ? tokens.shadow.card : 'none',
                            color: active ? tokens.ink.primary : tokens.ink.muted,
                          }}
                        >
                          {level.label}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
                ))}
              </Stack>
            )}

            {mode === 'plan' && planDraft && plan && tab === 'stripe' && (
              <Stack spacing={2.5}>
                {isFreePreviewPlan(plan) ? (
                  <InlineMessage severity="info">Free plans don&apos;t charge, so they don&apos;t need a Stripe price.</InlineMessage>
                ) : (
                  <>
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Monthly price</Typography>
                      <Typography sx={{ ...helperSx, mt: 0 }}>What Stripe charges subscribers every month.</Typography>
                      <Box sx={{ mt: 1 }}>
                        <StripePricePicker
                          options={priceOptionsFor('plan', stripeLinkCurrency(plan, planDraft.region))}
                          value={planDraft.stripePriceId}
                          onChange={id => setPlanDraft(d => (d ? { ...d, stripePriceId: id } : d))}
                          loading={stripePricesQuery.isLoading}
                          readError={stripePricesQuery.isError}
                          suggestion={suggestStripePrice(plan.name.trim().toLowerCase(), stripePrices, 'plan', stripeLinkCurrency(plan, planDraft.region))}
                          caption={`MONTHLY · ${stripeLinkCurrency(plan, planDraft.region)} · ${priceOptionsFor('plan', stripeLinkCurrency(plan, planDraft.region)).length} prices`}
                        />
                      </Box>
                      <Box sx={{ mt: 1.5 }}>
                        <Typography sx={inputLabelSx}>Region (optional)</Typography>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={planDraft.region}
                          onChange={e => setPlanDraft(d => (d ? { ...d, region: e.target.value } : d))}
                          slotProps={{ htmlInput: { 'aria-label': 'Region (optional)' }, select: { displayEmpty: true } }}
                        >
                          {regionOptions(planDraft.region).map(opt => (
                            <MenuItem key={opt.value || 'default'} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </TextField>
                        <Typography sx={helperSx}>
                          Leave blank for the default price. Use CA to link or clear the regional CAD price — live
                          billing data, read by every Canada-stack checkout.
                        </Typography>
                      </Box>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mt: 1.5, bgcolor: tokens.surface.sunken, borderRadius: '9px', p: '10px 12px' }}
                      >
                        <Typography sx={{ fontSize: 13 }}>Keep the display price in sync with Stripe</Typography>
                        <Switch
                          checked={planDraft.syncDisplayPrice}
                          onChange={e => setPlanDraft(d => (d ? { ...d, syncDisplayPrice: e.target.checked } : d))}
                        />
                      </Stack>
                    </Box>

                    <Box sx={{ borderTop: `1px solid ${tokens.line.hairline}`, pt: 2 }}>
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Setup fee</Typography>
                          <Typography sx={{ ...helperSx, mt: 0 }}>One-time charge on the first invoice.</Typography>
                        </Box>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>Custom quote per deal</Typography>
                          <Switch
                            checked={planDraft.setupFeeCustom}
                            onChange={e =>
                              setPlanDraft(d =>
                                d ? { ...d, setupFeeCustom: e.target.checked, setupFee: e.target.checked ? '' : d.setupFee } : d,
                              )
                            }
                          />
                        </Stack>
                      </Stack>
                      {!planDraft.setupFeeCustom && (
                        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                          <NumberField
                            aria-label="Setup fee"
                            min={0}
                            step={0.01}
                            adornment="$"
                            placeholder="Leave blank for no setup fee"
                            value={planDraft.setupFee}
                            onChange={value => setPlanDraft(d => (d ? { ...d, setupFee: value } : d))}
                            sx={{ width: '100%' }}
                          />
                          <StripePricePicker
                            // Filtered by the plan's own currency, not a
                            // hardcoded USD — the Canada stack's setup fees
                            // are CAD (CLAUDE.md "Subscription currency by
                            // stack"); see the PR description's conflict note.
                            options={priceOptionsFor('setup_fee', plan.currency || 'USD')}
                            value={planDraft.setupFeeStripePriceId}
                            onChange={id => setPlanDraft(d => (d ? { ...d, setupFeeStripePriceId: id } : d))}
                            loading={stripePricesQuery.isLoading}
                            readError={stripePricesQuery.isError}
                            suggestion={suggestStripePrice(plan.name.trim().toLowerCase(), stripePrices, 'setup_fee', plan.currency || 'USD')}
                            caption={`ONE-TIME · ${plan.currency || 'USD'} · ${priceOptionsFor('setup_fee', plan.currency || 'USD').length} prices`}
                          />
                        </Stack>
                      )}
                    </Box>
                  </>
                )}
              </Stack>
            )}

            {mode === 'plan' && plan && tab === 'subs' && (
              <Stack spacing={1.5}>
                {subscribers.length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: tokens.ink.faint, textAlign: 'center', py: 4 }}>
                    No stores are on this plan.
                  </Typography>
                ) : (
                  <Box sx={{ border: `1px solid ${tokens.line.default}`, borderRadius: '10px', overflow: 'hidden' }}>
                    {subscribers.map((row, i) => (
                      <Box
                        key={row.id}
                        sx={{
                          p: '12px 14px',
                          borderTop: i === 0 ? 'none' : `1px solid ${tokens.line.hairline}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1.5,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{row.organizationName}</Typography>
                          <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>
                            {row.currentPeriodEnd
                              ? `Renews ${new Date(row.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                              : 'No renewal date'}
                            {row.cancelAtPeriodEnd ? ' · ends at period end' : ''}
                          </Typography>
                        </Box>
                        <Stack alignItems="flex-end" spacing={0.5}>
                          <StatusChip
                            label={row.status}
                            tone={row.status === 'active' ? 'positive' : row.status === 'past_due' ? 'problem' : 'neutral'}
                          />
                          {row.stripeUrl && (
                            <Button
                              size="small"
                              endIcon={<OpenInNew fontSize="small" />}
                              href={row.stripeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontSize: 12, p: 0, minWidth: 0, textTransform: 'none' }}
                            >
                              Open in Stripe
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                )}
                <Button
                  variant="outlined"
                  onClick={() => plan && onOpenMoveSubscribers(plan)}
                  sx={{ alignSelf: 'flex-start', borderColor: tokens.line.default, color: tokens.ink.body, textTransform: 'none' }}
                >
                  Move subscribers to another plan
                </Button>
              </Stack>
            )}

            {mode === 'addon' && addon && addonDraft && (
              <Stack spacing={2.5}>
                <Box sx={{ bgcolor: tokens.surface.sunken, borderRadius: '9px', p: 1.5 }}>
                  <Typography sx={{ fontSize: 13 }}>
                    {formatAddonMoney(addon.price, addon.target.currency)}
                    <Box component="span" sx={{ color: tokens.ink.muted }}> {cadenceSuffix(addon.target.interval)} on the pricing sheet</Box>
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.ink.faint, mt: 0.5 }}>{availabilitySentence(addon)}</Typography>
                </Box>
                <Box>
                  <Typography sx={inputLabelSx}>Stripe price</Typography>
                  <StripePricePicker
                    options={priceOptionsForAddon(addon.target.interval, addon.target.currency)}
                    value={addonDraft.stripePriceId}
                    onChange={id => setAddonDraft(d => (d ? { ...d, stripePriceId: id } : d))}
                    loading={stripePricesQuery.isLoading}
                    readError={stripePricesQuery.isError}
                    caption={`${addon.target.interval === 'yearly' ? 'YEARLY' : 'MONTHLY'} · ${addon.target.currency} · ${priceOptionsForAddon(addon.target.interval, addon.target.currency).length} prices`}
                  />
                  <Typography sx={helperSx}>Missing a price? Create it in Stripe, then click Refresh.</Typography>
                </Box>
              </Stack>
            )}
          </Box>

          {/* Footer */}
          <Box sx={{ borderTop: `1px solid ${tokens.line.hairline}`, bgcolor: tokens.surface.sunken, p: '14px 22px' }}>
            {formError && (
              <Box sx={{ mb: 1.5 }}>
                <InlineMessage severity="error">{formError}</InlineMessage>
              </Box>
            )}
            <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
              <Button onClick={requestClose} disabled={saving} sx={{ textTransform: 'none', color: tokens.ink.body }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || (mode === 'plan' ? !planDraft?.name.trim() : false)}
                sx={{
                  height: 36,
                  fontSize: 13,
                  fontWeight: 700,
                  textTransform: 'none',
                  bgcolor: tokens.accent.main,
                  '&:hover': { bgcolor: tokens.accent.hover },
                }}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Drawer>

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard changes?"
        message="Your edits in this drawer haven't been saved."
        confirmLabel="Discard"
        confirmColor="warning"
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onClose();
        }}
        onClose={() => setDiscardConfirmOpen(false)}
      />

      <ConfirmDialog
        open={addonUnlinkConfirmOpen}
        title={`Unlink the Stripe price for "${addon?.target.name}"?`}
        message={
          <>
            Stores that already bought this add-on keep it and stay billed. New purchases stop until a price is
            linked again.
            {addonUnlinkError && (
              <Box sx={{ mt: 2 }}>
                <InlineMessage severity="error">{addonUnlinkError}</InlineMessage>
              </Box>
            )}
          </>
        }
        confirmLabel="Unlink"
        confirmColor="error"
        loading={saveAddonMutation.isPending}
        onConfirm={() => saveAddonMutation.mutate(null)}
        onClose={() => {
          setAddonUnlinkConfirmOpen(false);
          setAddonUnlinkError(null);
        }}
      />
    </>
  );
};

export default PlanEditorDrawer;
